import { useCallback } from 'react';

import { useApolloClient } from '@apollo/client/react';

import { CrmType } from '@anyx/common-graphql';


import { CustomerTagFilterValue } from '../components';
import { GET_CUSTOMER_TAGS, GET_CUSTOMER_TAGS_V2 } from '../graphql';

interface UseCustomerTagsProps {
  storeId: string;
  getCount?: boolean;
  crmType?: CrmType;
}

type GetFrequentlyUsedTagWithCount = () => Promise<CustomerTagFilterValue[]>;
type GetFrequentlyUsedTag = () => Promise<string[]>;

export type GetFrequentlyUsedTags = () => Promise<
  {
    label: string;
    value: string;
  }[]
>;

export function useCustomerTags(props: { storeId: string; getCount: true; crmType?: CrmType }): {
  getFrequentlyUsedTags: GetFrequentlyUsedTagWithCount;
};
export function useCustomerTags(props: { storeId: string; getCount: false; crmType?: CrmType }): {
  getFrequentlyUsedTags: GetFrequentlyUsedTag;
};
export function useCustomerTags(props: { storeId: string; crmType?: CrmType }): {
  getFrequentlyUsedTags: GetFrequentlyUsedTag;
};
export function useCustomerTags({ storeId, getCount = false, crmType }: UseCustomerTagsProps) {
  const apolloClient = useApolloClient();

  const getFrequentlyUsedTags = useCallback(async () => {
    try {
      const { data } = await apolloClient.query({
        query: GET_CUSTOMER_TAGS,
        variables: {
          input: {
            storeId: storeId,
            order: 'ByFrequentlyUsed',
            crmType,
          },
          getCount,
        },
      });

      const customerTags = data?.CustomerTags?.customerTags ?? [];
      if (!getCount) return customerTags.map(({ name }) => name);

      return customerTags.map(({ name, taggedCount }) => ({ name, taggedCount }));
    } catch (_) {
      return [];
    }
  }, [getCount, apolloClient, storeId, crmType]);

  return {
    getFrequentlyUsedTags,
  };
}

export function useCustomerTagsV2({ storeId, getCount = false }: UseCustomerTagsProps) {
  const apolloClient = useApolloClient();

  const getFrequentlyUsedTags = useCallback(async () => {
    try {
      const { data } = await apolloClient.query({
        query: GET_CUSTOMER_TAGS_V2,
        variables: {
          input: {
            storeId: storeId,
            order: 'ByFrequentlyUsed',
          },
          getCount,
        },
      });
      const customerTags = data?.CustomerTags?.customerTags ?? [];
      if (!getCount)
        return customerTags.map(({ id, name, isInfluencerTag }) => ({
          id,
          label: name,
          value: name,
          isInfluencerTag,
        }));
      return customerTags
        .filter((tag) => tag.taggedCount > 0)
        .map(({ id, name, taggedCount, isInfluencerTag }) => ({
          id,
          label: name,
          value: name,
          isInfluencerTag,
          taggedCount,
        }));
    } catch (_) {
      return [];
    }
  }, [getCount, apolloClient, storeId]);

  return {
    getFrequentlyUsedTags,
  };
}
